<template>
    <div class="payment-schedule">
        <div class="container-width">
            <h2 class="section-title">Mortgage payment schedule</h2>
            <p class="sub-heading">Use this chart to see how much principal and interest you are paying with each payment</p>

            <div class="payment-chart">
                <ul class="tab-name">
                    <li :class="{'active': MonthlyPayment}" @click="getPaymentFrequency('monthly')">
                        Monthly Payment
                    </li>
                    <li :class="{'active': BiWeeklyPayment}" @click="getPaymentFrequency('bi-weekly')">
                        Bi-Weekly Payment
                    </li>
                </ul>

                <div v-if="ChartMonthlyPayScheduleGraph || ChartWeeklyPayScheduleGraph" class="tab-content">
                    <div class="row" :class="{'active': MonthlyPayment}">
                        <div class="bar-chart">
                            <div>
                                <vue-chart v-if="MonthlyPayment" type="bar" :data="ChartMonthlyPayScheduleGraph" :width="ChartWidth" :height="ChartHeight" :options="tooltipOptions('tooltip-monthly-pay')" />
                            </div>
                        </div>
                    </div>
                    <div class="row" :class="{'active': BiWeeklyPayment}">
                        <div class="bar-chart">
                            <div>
                                <vue-chart v-if="BiWeeklyPayment" type="bar" :data="ChartWeeklyPayScheduleGraph" :width="ChartWidth" :height="ChartHeight" :options="tooltipOptions('tooltip-weekly-pay')" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="chart-details">
                <ul class="chart-info">
                    <li><span class="green-bg"></span> Principal</li>
                    <li><span class="blue-bg"></span> Interest</li>
                </ul>
            </div>
        </div>
    </div>
</template>

<script>
    import VueChart from 'vue-chart-js';
    import Accounting from 'accounting';

    export default {
        props: {
            payType: {
                default: 14,
            },
            loanTermInYears: {
                default: 0,
            },
            currentDate: {
                default: '',
            },
            allYearDeatilsForChart: {
                default: {},
            },
            paymentFrequency: {
                default: 'monthly',
            },
        },
        data() {
            return {
                MonthlyPayment: false,
                BiWeeklyPayment: false,
                ChartWidth: 1140,
                ChartHeight: 270,
                InterestAmt: this.interestAmt,
                LastDate: '',
                ChartMonthlyPayScheduleGraph: null,
                ChartWeeklyPayScheduleGraph: null,
            };
        },
        computed: {
            getYears() {
                const date = new Date(this.currentDate);
                const year = new Date(this.currentDate).getFullYear();
                const lastDate = new Date(date.setMonth(date.getMonth() + this.loanTermInYears * 12 - 1));
                this.chartMonthlyPaySchedule.labels = [];

                for (let i = 0; i <= lastDate.getFullYear() - year; i++) {
                    this.chartMonthlyPaySchedule.labels.push(year + i);
                }

                this.LastDate = lastDate;
                return this.chartMonthlyPaySchedule.labels.length;
            },
            chartMonthlyPaySchedule() {
                let result = {};
                if (this.allYearDeatilsForChart.years && this.allYearDeatilsForChart.years.length >= 0) {
                    if (this.paymentFrequency !== 'weekly') {
                        this.quicklyBlinkWithMonthlyPayment();
                    }
                    result = {
                        labels: this.allYearDeatilsForChart.years,
                        datasets: [
                            {
                                label: 'Principal',
                                backgroundColor: '#c2e8de',
                                hoverBackgroundColor: '#08c177',
                                data: this.allYearDeatilsForChart.principalAmounts,
                            },
                            {
                                label: 'Interest',
                                backgroundColor: '#99dff9',
                                hoverBackgroundColor: '#00aeef',
                                data: this.allYearDeatilsForChart.interestAmounts,
                            },
                        ],
                    };
                }
                return result;
            },
            chartWeeklyPaySchedule() {
                let result = {};
                if (this.allYearDeatilsForChart.years && this.allYearDeatilsForChart.years.length >= 0) {
                    if (this.paymentFrequency !== 'weekly') {
                        this.quicklyBlinkWithBiWeeklyPayment();
                    }
                    result = {
                        labels: this.allYearDeatilsForChart.years,
                        datasets: [
                            {
                                label: 'Principal',
                                backgroundColor: '#c2e8de',
                                hoverBackgroundColor: '#08c177',
                                data: this.allYearDeatilsForChart.principalAmounts,
                            },
                            {
                                label: 'Interest',
                                backgroundColor: '#99dff9',
                                hoverBackgroundColor: '#00aeef',
                                data: this.allYearDeatilsForChart.interestAmounts,
                            },
                        ],
                    };
                }
                return result;
            },
        },
        methods: {
            quicklyBlinkWithMonthlyPayment() {
                this.MonthlyPayment = false;
                setTimeout(() => {
                    this.MonthlyPayment = this.paymentFrequency === 'monthly';
                });
            },
            quicklyBlinkWithBiWeeklyPayment() {
                this.BiWeeklyPayment = false;
                setTimeout(() => {
                    this.BiWeeklyPayment = this.paymentFrequency === 'bi-weekly';
                }, 50);
            },
            tooltipOptions(type) {
                const otherExpenses = this.allYearDeatilsForChart.otherExpensesAmounts;
                return {
                    legend: {
                        display: false,
                    },
                    gridLines: false,
                    scales: {
                        yAxes: [{
                            ticks: {
                                beginAtZero: true,
                                callback(label) {
                                    return Accounting.formatMoney(`$${label}`, { precision: 0 });
                                },
                            },
                            afterTickToLabelConversion(scaleInstance) {
                                scaleInstance.ticks[scaleInstance.ticks.length - 1] = null;
                            },
                        }],
                        xAxes: [{
                            afterTickToLabelConversion(data) {
                                const xLabels = data.ticks;
                                xLabels.forEach((labels, i) => {
                                    if (i % 2 === 1) {
                                        xLabels[i] = '';
                                    }
                                });
                            },
                            gridLines: {
                                drawOnChartArea: false,
                            },
                        }],
                    },
                    responsive: false,
                    tooltips: {
                        enabled: false,
                        mode: 'index',
                        custom(tooltip) {
                            let tooltipEl = document.getElementById(type);
                            let principal;
                            const titleLines = tooltip.title || [];

                            if (!tooltipEl) {
                                tooltipEl = document.createElement('div');
                                tooltipEl.id = type;
                                tooltipEl.innerHTML = '<div></div>';
                                this._chart.canvas.parentNode.appendChild(tooltipEl);
                            }
                            if (tooltip.opacity === 0) {
                                tooltipEl.style.opacity = 0;
                                return;
                            }
                            tooltip.displayColors = false;
                            tooltipEl.classList.remove('above', 'below', 'no-transform');
                            if (tooltip.yAlign) {
                                tooltipEl.classList.add(tooltip.yAlign);
                            } else {
                                tooltipEl.classList.add('no-transform');
                            }
                            function getBody(bodyItem) {
                                return bodyItem.lines;
                            }
                            if (tooltip.body) {
                                let innerHtml = '';
                                const bodyLines = tooltip.body.map(getBody);
                                titleLines.forEach((title) => {
                                    innerHtml += `<h5>${title}</h5>`;
                                });

                                principal = bodyLines[0][0].split(':');
                                innerHtml += `<label>${principal[0]}: <b class="green-text">${Accounting.formatMoney(principal[1], { precision: 2 })}</b></label>`;

                                const interest = bodyLines[1][0].split(':');
                                innerHtml += `<label>${interest[0]}: <b class="blue-text">${Accounting.formatMoney(interest[1], { precision: 2 })}</b></label>`;

                                innerHtml += `<label>Tax & Fees: <b>${Accounting.formatMoney(otherExpenses[titleLines[0]], { precision: 2 })}</b></label>`;
                                const tableRoot = tooltipEl.querySelector('div');
                                tableRoot.innerHTML = innerHtml;
                            }
                            const positionY = this._chart.canvas.offsetTop;
                            const positionX = this._chart.canvas.offsetLeft;
                            let width = '150px';
                            if (otherExpenses && otherExpenses[titleLines[0]]) {
                                let TaxValue = parseInt(otherExpenses[titleLines[0]][0], 10);
                                const principalValue = parseInt(principal[1], 10);
                                const lengthDifference = principalValue.toString().length - TaxValue.toString().length;
                                switch (lengthDifference) {
                                    case 3:
                                        TaxValue += '99';
                                        break;
                                    case 4:
                                        TaxValue += '999';
                                        break;
                                    case 5:
                                        TaxValue += '9999';
                                        break;
                                    case 6:
                                        TaxValue += '9999';
                                        break;
                                    case 7:
                                        TaxValue += '99999';
                                        break;
                                    case 8:
                                        TaxValue += '9999999';
                                        break;
                                    case 9:
                                        TaxValue += '99999999';
                                        break;
                                    case 10:
                                        TaxValue += '99999999';
                                        break;
                                    case 11:
                                        TaxValue += '999999999';
                                        break;
                                    case 12:
                                        TaxValue += '9999999999';
                                        break;
                                    default:
                                        TaxValue += '999999';
                                        break;
                                }
                                if (TaxValue) {
                                    switch (TaxValue.toString().length) {
                                        case 6:
                                            width = '160px';
                                            break;
                                        case 7:
                                            width = '170px';
                                            break;
                                        case 8:
                                            width = '178px';
                                            break;
                                        case 9:
                                            width = '185px';
                                            break;
                                        case 10:
                                            width = '195px';
                                            break;
                                        case 11:
                                            width = '202px';
                                            break;
                                        case 12:
                                            width = '205px';
                                            break;
                                        case 13:
                                            width = '215px';
                                            break;
                                        default:
                                            width = '150px';
                                            break;
                                    }
                                }
                            }
                            tooltipEl.style.opacity = 1;
                            tooltipEl.style.left = `${positionX + tooltip.caretX}px`;
                            tooltipEl.style.top = `${positionY - 110 + tooltip.caretY}px`;
                            tooltipEl.style.height = '100px';
                            tooltipEl.style.width = width;
                        },
                    },
                };
            },
            getPaymentFrequency(frequency) {
                this.$emit('getPaymentFrequency', frequency);
            },
        },
        components: {
            VueChart,
        },
        mixins: [Accounting],
        watch: {
            chartMonthlyPaySchedule() {
                this.ChartMonthlyPayScheduleGraph = this.chartMonthlyPaySchedule;
            },
            chartWeeklyPaySchedule() {
                this.ChartWeeklyPayScheduleGraph = this.chartWeeklyPaySchedule;
            },
            paymentFrequency() {
                if (this.paymentFrequency !== 'weekly') {
                    this.MonthlyPayment = this.paymentFrequency === 'monthly';
                    this.BiWeeklyPayment = this.paymentFrequency === 'bi-weekly';
                }
            },
        },
    };
</script>
<style lang="scss" src="../../../assets/styles/amortization-breakdown-graph.scss"></style>
